/**
 * WebMCP Client Hook & Lifecycle Manager
 * Registers in-browser tools with navigator.modelContext
 */

export function initWebMcp(): () => void {
  if (typeof window === "undefined") return () => {};

  const controller = new AbortController();

  if (!("modelContext" in navigator) || !(navigator as any).modelContext) {
    const toolRegistry = new Map<string, any>();
    (navigator as any).modelContext = {
      tools: toolRegistry,
      registerTool(tool: any, options?: { signal?: AbortSignal }) {
        if (!tool || !tool.name) return;
        toolRegistry.set(tool.name, tool);
        if (options?.signal) {
          options.signal.addEventListener("abort", () => {
            toolRegistry.delete(tool.name);
          });
        }
      },
      unregisterTool(name: string) {
        toolRegistry.delete(name);
      }
    };
  }

  const mc = (navigator as any).modelContext;
  if (!mc || typeof mc.registerTool !== "function") return () => {};

  try {
    mc.registerTool(
      {
        name: "analyze_text",
        description: "Evaluates writing samples using CatchAI Text computational stylometry (burstiness, entropy, and AI probability).",
        inputSchema: {
          type: "object",
          properties: {
            text: {
              type: "string",
              description: "The writing sample or essay to evaluate for machine generation."
            }
          },
          required: ["text"]
        },
        execute: async (args: { text: string }) => {
          try {
            const response = await fetch("/api/detect", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ text: args.text })
            });
            return await response.json();
          } catch (err: any) {
            return { error: err.message || "Failed to analyze text" };
          }
        }
      },
      { signal: controller.signal }
    );

    mc.registerTool(
      {
        name: "get_model_weights",
        description: "Retrieves active logistic regression model weights and classification thresholds.",
        inputSchema: {
          type: "object",
          properties: {}
        },
        execute: async () => {
          try {
            const response = await fetch("/api/weights");
            return await response.json();
          } catch (err: any) {
            return { error: err.message || "Failed to fetch model weights" };
          }
        }
      },
      { signal: controller.signal }
    );

    mc.registerTool(
      {
        name: "search_faq",
        description: "Searches CatchAI Text methodology, student rights, and academic integrity policies.",
        inputSchema: {
          type: "object",
          properties: {
            query: {
              type: "string",
              description: "Keywords or questions regarding scoring and integrity."
            }
          },
          required: ["query"]
        },
        execute: async (args: { query: string }) => {
          return {
            query: args.query,
            policy: "Diagnostic indicator only; requires human pedagogical review.",
            privacy: "Zero data retention in-memory processing."
          };
        }
      },
      { signal: controller.signal }
    );
  } catch (e) {
    console.debug("[WebMCP] Registration note:", e);
  }

  return () => {
    controller.abort();
  };
}
